import { grey } from "@mui/material/colors";
import {
  BigDotIcon,
  Box,
  Divider,
  Email1Icon,
  FacebookIcon,
  Grid2,
  IconButton,
  InstagramIcon,
  LinkedinIcon,
  LocationIcon,
  Phone1Icon,
  TwitterIcon,
  Typography,
  useMediaQuery,
  YoutubeIcon,
} from "components";
import GKEditorViewer from "components/GKEditorViewer";
import { useFormContext, useWatch } from "react-hook-form";
import { Link } from "react-router-dom";
import { useAppSelector } from "store/store";
import { ThemeContainer } from "utils/Theme";
import { WebsiteDetails } from "../types";

const footerLink = [
  { to: "#", name: "Investment Charter" },
  { to: "#", name: "Terms & Conditions" },
  { to: "#", name: "Privacy Policies" },
  { to: "#", name: "Pricing Policies" },
  { to: "#", name: "Refund Policies" },
  { to: "#", name: "Disclosures" },
  { to: "#", name: "Complaints Resolution" },
  { to: "#", name: "Compliance Audit Report" },
  { to: "#", name: "Grievance & Compliance Officer Details" },
];

const FooterSection = () => {
  const { theme } = ThemeContainer();

  const form = useFormContext<WebsiteDetails>();

  const focusedField = useAppSelector(
    (state) => state?.myWebsite?.focusedField,
  );

  const [footer, myBranding, heroSection] = useWatch({
    control: form.control,
    name: [
      "websiteSection.footer",
      "websiteSection.myBranding",
      "websiteSection.heroSection",
    ],
  });

  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  const linksData = [
    { icon: FacebookIcon, url: "#" },
    { icon: LinkedinIcon, url: "#" },
    { icon: TwitterIcon, url: "#" },
    { icon: YoutubeIcon, url: "#" },
    { icon: InstagramIcon, url: "#" },
  ];

  return (
    <Grid2
      container
      width={"100%"}
      columnSpacing={5}
      rowSpacing={3}
      maxWidth={{ xs: "100vw", md: "1192px" }}
    >
      <Grid2 size={{ xs: 12 }} container spacing={1}>
        <Grid2 size={12}>
          <img
            alt=""
            height={50}
            width={"auto"}
            src={myBranding?.companyLogo as string}
          />
        </Grid2>
        <Grid2
          size={12}
          sx={{
            "& .ck p span": {
              color: `${theme.palette.dark.main} !important`,
              background: "transparent !important",
            },
            "& .ck p": {
              fontSize: 16,
              fontWeight: 700,
              color: `${theme.palette.primary.main} !important`,
            },
          }}
        >
          <GKEditorViewer content={heroSection?.companyMetaName} />
          <Box
            width={"100%"}
            display={"flex"}
            alignItems={{
              xs: "flex-start",
              md: "center",
            }}
            flexDirection={{
              xs: "column",
              md: "row",
            }}
            gap={1.5}
          >
            <Typography
              fontSize={12}
              color="var(--neutral-500)"
              width={"max-content"}
              borderRadius={
                focusedField === "websiteSection.footer.sebiRegistered"
                  ? 2
                  : undefined
              }
              border={
                focusedField === "websiteSection.footer.sebiRegistered"
                  ? "2px solid #007bff"
                  : undefined
              }
            >
              SEBI Registration No:{" "}
              <span style={{ color: "var(--grey-700)", fontWeight: 600 }}>
                {footer?.sebiRegistered}
              </span>
            </Typography>
            {mdUp && (
              <BigDotIcon color="var(--neutral-300)" width={6} height={6} />
            )}
            <Typography
              fontSize={12}
              color="var(--neutral-500)"
              width={"max-content"}
              borderRadius={
                focusedField ===
                "websiteSection.footer.sebiRegisteredSupervisoryBody"
                  ? 2
                  : undefined
              }
              border={
                focusedField ===
                "websiteSection.footer.sebiRegisteredSupervisoryBody"
                  ? "2px solid #007bff"
                  : undefined
              }
            >
              SEBI Registered Supervisor Body:{" "}
              <span style={{ color: "var(--grey-700)", fontWeight: 600 }}>
                {footer?.sebiRegisteredSupervisoryBody || "N/A"}
              </span>
            </Typography>
            {mdUp && (
              <BigDotIcon color="var(--neutral-300)" width={6} height={6} />
            )}
            <Typography
              fontSize={12}
              color="var(--neutral-500)"
              width={"max-content"}
              borderRadius={
                focusedField ===
                "websiteSection.footer.supervisoryBodyMembershipId"
                  ? 2
                  : undefined
              }
              border={
                focusedField ===
                "websiteSection.footer.supervisoryBodyMembershipId"
                  ? "2px solid #007bff"
                  : undefined
              }
            >
              Membership No:{" "}
              <span style={{ color: "var(--grey-700)", fontWeight: 600 }}>
                {footer?.supervisoryBodyMembershipId || "N/A"}
              </span>
            </Typography>
          </Box>
        </Grid2>
        <Grid2 size={12}>
          <Typography fontSize={14} color="#000000" fontWeight={500}>
            {heroSection?.advisorName}, RA
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2
        size={{ xs: 12, md: 5 }}
        container
        spacing={"12px"}
        alignContent={"flex-start"}
      >
        <Grid2
          size={12}
          sx={{
            "& .ck p": {
              color: "var(--grey-800)",
              fontWeight: 600,
              fontSize: 18,
            },
            border:
              focusedField === "websiteSection.footer.footerTagLine"
                ? "2px solid #007bff"
                : undefined,
          }}
          borderRadius={
            focusedField === "websiteSection.footer.footerTagLine"
              ? 2
              : undefined
          }
        >
          <GKEditorViewer
            content={
              footer?.footerTagLine ||
              "Making digital investing possible with us!"
            }
          />
        </Grid2>
        <Grid2
          size={12}
          sx={{
            "& .ck p": {
              fontSize: 12,
              color: "var(--grey-700)",
            },
            border:
              focusedField === "websiteSection.footer.footerTagLineBody"
                ? "2px solid #007bff"
                : undefined,
          }}
          borderRadius={
            focusedField === "websiteSection.footer.footerTagLineBody"
              ? 2
              : undefined
          }
        >
          <GKEditorViewer
            content={
              footer?.footerTagLineBody ||
              "Our company is a SEBI Registered Research Analyst Firm with a Dedicated Research Team specializing in unearthing high quality undervalued stocks. We focus on few high conviction stocks that exhibit chances to outperform and have out sized impact on the portfolio."
            }
          />
        </Grid2>
        <Grid2 size={12}>
          <Typography
            fontSize={11}
            color="var(--neutral-500)"
            fontStyle={"italic"}
            fontWeight={400}
          >
            Investments in securities market are subject to market risks. Read
            all the related documents carefully before investing. Registration
            granted by SEBI, membership of BASL (in case of IAs) and
            certification from NISM in no way guarantee performance of the
            intermediary or provide any assurance of returns to investors{" "}
          </Typography>
        </Grid2>
      </Grid2>
      <Grid2
        size={{ xs: 12, md: 3.3 }}
        display={"flex"}
        alignItems={"flex-start"}
        flexDirection={"column"}
        gap={0.5}
      >
        <Grid2 size={12}>
          <Typography fontWeight={700}>Fine Prints:</Typography>
        </Grid2>
        {footerLink.map((item, index) => {
          return (
            <Link
              key={index}
              to={item.to}
              style={{
                textDecoration: "none",
                color: "var(--grey-700)",
              }}
            >
              {item.name}{" "}
            </Link>
          );
        })}
      </Grid2>
      <Grid2
        size={{ xs: 12, md: 3.7 }}
        container
        spacing={0}
        alignContent={"flex-start"}
        pl={{ xs: 0, md: 8 }}
      >
        <Grid2 size={12}>
          <Typography fontWeight={700}>Contact Details:</Typography>
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          {[
            {
              name: "Phone No.",
              value: footer?.advisorPhone,
              icon: Phone1Icon,
              key: "websiteSection.footer.advisorPhone",
            },
            {
              name: "Email Id",
              value: footer?.advisorEmail,
              icon: Email1Icon,
              key: "websiteSection.footer.advisorEmail",
            },
            {
              name: "Address",
              value: footer?.advisorAddress,
              icon: LocationIcon,
              key: "websiteSection.footer.advisorAddress",
            },
          ]?.map((item, index) => {
            return (
              <Typography
                key={index}
                color={grey[700]}
                mt={2}
                sx={{
                  display: "flex",
                  alignItems: "start",
                  gap: 2,
                }}
              >
                <span
                  style={{
                    background: `#ECECEC`,
                    minWidth: 35,
                    minHeight: 35,
                    borderRadius: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <item.icon height={20} width={20} />
                </span>
                <span style={{ width: "calc(100% - 40px)" }}>
                  <Typography fontSize={12}>{item?.name}</Typography>
                  <Typography
                    fontSize={14}
                    color="#000000"
                    borderRadius={focusedField === item.key ? 2 : undefined}
                    border={
                      focusedField === item.key
                        ? "2px solid #007bff"
                        : undefined
                    }
                  >
                    {item?.value}
                  </Typography>
                </span>
              </Typography>
            );
          })}
        </Grid2>
      </Grid2>
      <Grid2 size={{ xs: 12, md: 9 }} />
      <Grid2 size={{ xs: 12, md: 3 }}>
        {linksData.map((d, i) => (
          <IconButton
            href={d.url}
            target="_blank"
            key={i}
            style={{ marginRight: 8 }}
          >
            <d.icon color={grey[700]} width={24} height={24} />
          </IconButton>
        ))}
      </Grid2>
      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2 size={12}>
        <Typography color="#697586">
          Copyright © 2024 Gridkey Technologies Private Limited. All rights
          reserved.
        </Typography>
      </Grid2>
    </Grid2>
  );
};

export default FooterSection;
